/**
 * useAuth Hook
 * 
 * Provides authentication utilities and session management.
 */

import { STORAGE_KEYS } from '../../../shared/utils/constants';
import { clearToken, readToken, writeToken } from '../../../shared/auth/session';

/**
 * useAuth hook for authentication operations
 * @returns {Object} Auth utilities
 */
export function useAuth() {
  /**
   * Store session data in sessionStorage
   * @param {Object} data - Authentication response data
   */
  const storeSession = (data) => {
    if (!data) return;

    // CRITICAL: Clear all organization-specific cached data BEFORE storing new session
    // This ensures data from previous organization doesn't persist when switching accounts
    sessionStorage.removeItem(STORAGE_KEYS.ORCHESTRATIONS);
    sessionStorage.removeItem(STORAGE_KEYS.PROJECTS);
    sessionStorage.removeItem('listOrchestrations');
    localStorage.removeItem('activeEnvironment');

    // Store user ID
    if (data.user_id) {
      sessionStorage.setItem(STORAGE_KEYS.USERID, data.user_id);
    }

    // Store token
    if (data.token) {
      writeToken(data.token);
    }

    // Store username
    if (data.name) {
      sessionStorage.setItem(STORAGE_KEYS.USERNAME, data.name);
    }

    // Store organization info
    if (data.organization_id) {
      sessionStorage.setItem(STORAGE_KEYS.ORGANIZATION_ID, data.organization_id);
    }
    if (data.organization_name) {
      sessionStorage.setItem(STORAGE_KEYS.ORGANIZATION_NAME, data.organization_name);
    }
    if (data.organization_role) {
      sessionStorage.setItem(STORAGE_KEYS.ORGANIZATION_ROLE, data.organization_role);
    }
    if (data.invite_status) {
      sessionStorage.setItem(STORAGE_KEYS.INVITE_STATUS, data.invite_status);
    }

    // Store role
    if (data.roles && Array.isArray(data.roles)) {
      const role = data.roles.includes('ADMIN') ? 'Admin' : 'User';
      sessionStorage.setItem(STORAGE_KEYS.ROLE, role);
    }
  };

  /**
   * Clear session data from sessionStorage
   */
  const clearSession = () => {
    // Clear user session data
    sessionStorage.removeItem(STORAGE_KEYS.USERID);
    clearToken();
    sessionStorage.removeItem(STORAGE_KEYS.USERNAME);
    sessionStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ID);
    sessionStorage.removeItem(STORAGE_KEYS.ORGANIZATION_NAME);
    sessionStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ROLE);
    sessionStorage.removeItem(STORAGE_KEYS.INVITE_STATUS);
    sessionStorage.removeItem(STORAGE_KEYS.ROLE);
    
    // Clear organization-specific cached data
    sessionStorage.removeItem(STORAGE_KEYS.ORCHESTRATIONS);
    sessionStorage.removeItem(STORAGE_KEYS.PROJECTS);
    sessionStorage.removeItem('listOrchestrations');
    localStorage.removeItem('activeEnvironment');
  };

  /**
   * Check if user is authenticated
   * @returns {boolean} True if user has a valid token
   */
  const isAuthenticated = () => {
    const token = readToken();
    return !!token;
  };

  return {
    storeSession,
    clearSession,
    isAuthenticated,
  };
}

export default useAuth;
